import {createFeatureSelector, createSelector} from "@ngrx/store";
import {ALL_CASE_TOKENS, DealSensitivityState} from "./deal-sensitivity.state";
import {dealSensitivityStoreFeatureKey} from "./deal-sensitivity.reducer";
import {ScenarioType} from "../../../shared/model/scenario-type";
import {DealCashflowsUtil} from "../../utils/deal-cashflows-util";
import {selectSettingState} from "../../../shared/user-setting/store/user-setting.selectors";
import {CASHFLOW_WEIGHTING_KEY} from "../../../settings/settings-keys";

// Maps each perf token to the actual metric column suffixes it covers
const PERF_TOKEN_MAP: Record<string, string[]> = {
    netPerf: ["netPerformanceTvpi", "netPerformanceIrr"],
    grossPerf: ["grossPerformanceTvpi", "grossPerformanceIrr"],
    navUplift: ["navUplift"],
};

// Column def names for the case-level group header row (groupsScnHeader)
const CASE_SCN_HEADER: Record<string, string> = {
    weighted: "groupsWeighted",
    low: "groupsLow",
    base: "groupsBase",
    high: "groupsHigh",
};

// Column def name for the perf-group header within a case (groupsHeader)
function perfGroupHeaderKey(caseKey: string, perfToken: string): string {
    const casePart = caseKey === "weighted" ? "" : caseKey.charAt(0).toUpperCase() + caseKey.slice(1);
    const perfMap: Record<string, string> = {
        netPerf: "NetPerformance",
        grossPerf: "GrossPerformance",
        navUplift: "NavUplift",
    };
    return `groups${casePart}${perfMap[perfToken]}`;
}

// Derives the actual table column ID from a case key + metric suffix
function colId(caseKey: string, metric: string): string {
    if (caseKey === "weighted") return metric;
    return caseKey + metric.charAt(0).toUpperCase() + metric.slice(1);
}

export interface SensitivityColspanMap {
    [colId: string]: number;
}

export const selectDealSensitivityFeature = createFeatureSelector<DealSensitivityState>(dealSensitivityStoreFeatureKey);

export const selectDealSensitivityOverview = createSelector(
    selectDealSensitivityFeature,
    (state) => state.dealSensitivityOverview
);

export const selectDealSensitivities = createSelector(
    selectDealSensitivityOverview,
    (apiData) => apiData.data.dealSensitivities ?? []
);

export const selectDealSensitivityLoading = createSelector(
    selectDealSensitivityOverview,
    (apiData) => apiData.status === "LOADING"
);

export const selectDealSensitivityOverviewError = createSelector(
    selectDealSensitivityOverview,
    (apiData) => apiData.status === "ERROR" ? apiData.errorMessage : ""
);

export const selectDealSensitivityOutdated = createSelector(
    selectDealSensitivityOverview,
    (apiData) => apiData.data.outdated === true
);

export const selectDealSensitivityDetails = createSelector(
    selectDealSensitivityFeature,
    (state) => state.dealSensitivity
);

export const selectDealSensitivityDetailsData = createSelector(
    selectDealSensitivityDetails,
    (apiData) => apiData.data
);

export const selectDealSensitivityDetailsLoading = createSelector(
    selectDealSensitivityDetails,
    (apiData) => apiData.status === "LOADING"
);

export const selectColumnTokens = createSelector(
    selectDealSensitivityFeature,
    (state) => state.selectedColumnTokens
);

export const selectSensitivityDisplayedColumns = createSelector(
    selectColumnTokens,
    (tokens) => {
        const cols = ["name"];
        for (const token of tokens) {
            const sep = token.indexOf("_");
            const caseKey = token.substring(0, sep);
            const perfToken = token.substring(sep + 1);
            for (const metric of PERF_TOKEN_MAP[perfToken] ?? []) {
                cols.push(colId(caseKey, metric));
            }
        }
        return cols;
    }
);

export const selectSensitivityGroupsHeader = createSelector(
    selectColumnTokens,
    (tokens) => {
        const header = ["groupsName"];
        for (const token of tokens) {
            const sep = token.indexOf("_");
            const caseKey = token.substring(0, sep);
            const perfToken = token.substring(sep + 1);
            header.push(perfGroupHeaderKey(caseKey, perfToken));
        }
        return header;
    }
);

export const selectSensitivityGroupsScnHeader = createSelector(
    selectColumnTokens,
    (tokens) => {
        const header = ["groupsScnName"];
        for (const caseKey of ALL_CASE_TOKENS) {
            if (tokens.some(t => t.startsWith(caseKey + "_"))) {
                header.push(CASE_SCN_HEADER[caseKey]);
            }
        }
        return header;
    }
);

export const selectSensitivityColspanMap = createSelector(
    selectColumnTokens,
    (tokens) => {
        const map: SensitivityColspanMap = {};
        for (const token of tokens) {
            const sep = token.indexOf("_");
            const caseKey = token.substring(0, sep);
            const perfToken = token.substring(sep + 1);
            const metricCount = (PERF_TOKEN_MAP[perfToken] ?? []).length;
            const perfHeaderKey = perfGroupHeaderKey(caseKey, perfToken);
            map[perfHeaderKey] = metricCount;
            const caseHeaderKey = CASE_SCN_HEADER[caseKey];
            map[caseHeaderKey] = (map[caseHeaderKey] ?? 0) + metricCount;
        }
        return map;
    }
);

// Identifies the perf-group header and first metric column that start each case
// section, so the case-separator left-border follows the currently-first column.
export const selectSensitivityLeftBorderKeys = createSelector(
    selectColumnTokens,
    (tokens) => {
        const headerKeys = new Set<string>();
        const colKeys = new Set<string>();
        for (const caseKey of ALL_CASE_TOKENS) {
            const firstToken = tokens.find(t => t.startsWith(caseKey + "_"));
            if (!firstToken) continue;
            const perfToken = firstToken.substring(firstToken.indexOf("_") + 1);
            headerKeys.add(perfGroupHeaderKey(caseKey, perfToken));
            const firstMetric = (PERF_TOKEN_MAP[perfToken] ?? [])[0];
            if (firstMetric) colKeys.add(colId(caseKey, firstMetric));
        }
        return {headerKeys, colKeys};
    }
);

// Returns the loaded sensitivity's aggregated cashflows for the requested scenario,
// wrapped in the same {aggregatedDealCashFlows} shape that the live-deal selector
// uses — so downstream cashflow components can swap source without changing shape.
export const selectDealSensitivityDetailsCashflowForScnType = (scenarioType: ScenarioType) =>
    createSelector(
        selectDealSensitivityDetailsData,
        (details) => {
            let agg;
            if (scenarioType === "LOW") agg = details?.aggregatedDealCashflowsLow;
            else if (scenarioType === "BASE") agg = details?.aggregatedDealCashflowsBase;
            else if (scenarioType === "HIGH") agg = details?.aggregatedDealCashflowsHigh;
            else if (scenarioType === "WEIGHTED") agg = details?.aggregatedDealCashflowsWeighted;
            return {aggregatedDealCashFlows: agg};
        }
    );

// Builds the table model the details return-summary table renders: 4 case rows
// (Low / Base / High / CF weighted case) and the colspan-driven 2-row grouped header.
type ScenarioKey = "low" | "base" | "high" | "weighted";

const RETURN_SUMMARY_ORDER: Array<{ key: ScenarioKey; label: string }> = [
    {key: "low", label: "Low"},
    {key: "base", label: "Base"},
    {key: "high", label: "High"},
    {key: "weighted", label: "CF weighted case"},
];

export const selectDealSensitivityDetailsReturnSummaryTable = createSelector(
    selectDealSensitivityDetailsData,
    selectSettingState(CASHFLOW_WEIGHTING_KEY),
    (details, weightingSetting) => {
        const summary = details?.sensitivityReturnSummary;
        const rows: any[] = [];
        for (const {key, label} of RETURN_SUMMARY_ORDER) {
            const c = summary?.[key];
            if (!c) continue;
            rows.push({
                scenarioKey: key,
                scenarioLabel: label,
                probability: c.probability,
                netPerformanceTvpi: c.netPerformanceTvpi,
                netPerformanceIrr: c.netPerformanceIrr,
                grossPerformanceTvpi: c.grossPerformanceTvpi,
                grossPerformanceIrr: c.grossPerformanceIrr,
                navUplift: c.navUplift,
                isWeighted: key === "weighted",
            });
        }
        const displayedColumns = [
            "scenarioLabel",
            "probability",
            "netPerformanceTvpi",
            "netPerformanceIrr",
            "grossPerformanceTvpi",
            "grossPerformanceIrr",
            "navUplift",
        ];
        const headerGroups = [
            {label: "", colspan: 1},                  // Scenario
            {label: "", colspan: 1},                  // Probability
            {label: "Net Performance", colspan: 2},   // TVPI, IRR
            {label: "Gross Performance", colspan: 2}, // TVPI, IRR
            {label: "NAV", colspan: 1},               // Uplift
        ];
        // WI 1085: fund valuations differing from the settings default — the "various" explanation.
        const weightingDivergences = summary?.weightingDivergences ?? [];
        // WI 1085: the weighting is "various" only when the per-scenario probabilities can't collapse to a
        // single shared triple — the backend leaves their fraction null in that case. Mirror the guard on
        // deal-return-summary: the header popover explains the "various" state, so only surface it when the
        // state is actually "various". Otherwise a consistent but non-default triple (divergences are
        // populated by comparing against the settings default) would wrongly show the "why various?" icon.
        const isWeightingVarious = rows.some(r => !r.isWeighted && r.probability?.fraction === null);
        // Whether users may override the org weighting default — drives the popover's settings deep-link.
        const weightingUserOverridable = weightingSetting?.overridable ?? false;
        return {rows, displayedColumns, headerGroups, weightingDivergences, isWeightingVarious, weightingUserOverridable};
    }
);

// Graph-shaped variants — match the colors used by selectLow/Base/HighScenarioDealCashflowsGraph.
export const selectDealSensitivityDetailsLowScenarioCashflowsGraph = createSelector(
    selectDealSensitivityDetailsCashflowForScnType("LOW"),
    (cashflows) => DealCashflowsUtil.createChartData(cashflows, "#D47474")
);

export const selectDealSensitivityDetailsBaseScenarioCashflowsGraph = createSelector(
    selectDealSensitivityDetailsCashflowForScnType("BASE"),
    (cashflows) => DealCashflowsUtil.createChartData(cashflows, "#E8A15B")
);

export const selectDealSensitivityDetailsHighScenarioCashflowsGraph = createSelector(
    selectDealSensitivityDetailsCashflowForScnType("HIGH"),
    (cashflows) => DealCashflowsUtil.createChartData(cashflows, "#6FBF73")
);

export const selectDealSensitivityDetailsWeightedScenarioCashflowsGraph = createSelector(
    selectDealSensitivityDetailsCashflowForScnType("WEIGHTED"),
    (cashflows) => DealCashflowsUtil.createChartData(cashflows, "grey")
);
